import type { ReactNode } from 'react'
import type { SipPlanRecommendationDetails } from '../../api/sipPlanRecommendation.types'
import { SipCase } from '../../api/sipPlanRecommendation.types'
import {
  ConsistencyScorePointer,
  PowerRankPointer,
  RecencyScorePointer,
  VolatilityScorePointer
} from '../../../../plans-ui-components/PlanRecommendationScoreReasonPointers'
import {
  PlanRecommendationReasonPointer,
  resolvePlanRecommendationReasonPointerBadgeLabel
} from '../../../../plans-ui-components/PlanRecommendationReasonPointer'

type SipStopPlanRecommendationBodyProps = {
  planDetails: SipPlanRecommendationDetails | null
}

export function SipStopPlanRecommendationBody({
  planDetails
}: SipStopPlanRecommendationBodyProps) {
  const casePointers = renderSipStopCasePointers(planDetails)

  return (
    <div>
      <div className="h-[10px] bg-[#F0F4F7]" />
      <div className="pt-6 pb-2">
        <div className="px-5 pb-2 text-[18px] font-[500] leading-[130%] text-[#232323]">Reasons to stop SIP</div>
        {casePointers != null ? <div className="pt-4">{casePointers}</div> : null}
      </div>
    </div>
  )
}

function renderSipStopCasePointers(planDetails: SipPlanRecommendationDetails | null): ReactNode {
  const sipRecommendationCase = planDetails?.sipRecommendationCase
  if (sipRecommendationCase == null) {
    return null
  }

  const casePointersBySipCase: Partial<Record<SipCase, ReactNode>> = {
    [SipCase.STOP_UNDER_PERFORMING_FUND_WITH_EXPOSURE]: (
      <SipStopPerformancePointers planDetails={planDetails} startIndex={1} />
    ),
    [SipCase.STOP_UNDER_PERFORMING_FUND_WITHOUT_EXPOSURE]: (
      <>
        <SipStopRemoveCategoryPointer planDetails={planDetails} index={1} />
        <SipStopPerformancePointers planDetails={planDetails} startIndex={2} />
      </>
    ),
    [SipCase.STOP_WELL_PERFORMING_FUND_WITHOUT_EXPOSURE]: (
      <SipStopRemoveCategoryPointer
        planDetails={planDetails}
        index={1}
        badgeLabel={resolvePlanRecommendationReasonPointerBadgeLabel({
          totalPointerCount: 1,
          showsPercentageHighlight: true
        })}
      />
    ),
    [SipCase.STOP_IN_FORM_FUND_WITH_EXPOSURE]: (
      <SipStopBetterFundPointer
        planDetails={planDetails}
        subtitle="This fund is already in good shape as it is In-form and well-ranked. But since you're investing regularly through SIPs, it's better to invest in one of the top funds instead."
      />
    ),
    [SipCase.STOP_ON_TRACK_FUND_WITH_EXPOSURE]: (
      <SipStopBetterFundPointer
        planDetails={planDetails}
        subtitle="This fund is on-track, but there are stronger, in-form funds in this category. We'd recommend directing your SIP to one of them instead."
      />
    )
  }

  return casePointersBySipCase[sipRecommendationCase] ?? null
}

type SipStopRemoveCategoryPointerProps = {
  planDetails: SipPlanRecommendationDetails | null
  index: number
  badgeLabel?: string | number
}

export function SipStopRemoveCategoryPointer({
  planDetails,
  index,
  badgeLabel
}: SipStopRemoveCategoryPointerProps) {
  const categoryLabel = (planDetails?.categoryName ?? '').trim()
  if (categoryLabel.length <= 0) {
    return null
  }

  return (
    <PlanRecommendationReasonPointer
      index={index}
      badgeLabel={badgeLabel}
      title={`SIPs in ${categoryLabel} category not needed,`}
      subtitle="as per allocation strategy"
    />
  )
}

type SipStopPerformancePointersProps = {
  planDetails: SipPlanRecommendationDetails | null
  startIndex: number
}

export function SipStopPerformancePointers({
  planDetails,
  startIndex
}: SipStopPerformancePointersProps) {
  const categoryLabel = planDetails?.categoryName ?? ''
  const scoringData = planDetails?.scoringData ?? null
  const maxScore = scoringData?.maxScore ?? 100
  const pointerKinds: Array<'POWER_RANK' | 'CONSISTENCY_SCORE' | 'RECENCY_SCORE' | 'VOLATILITY_SCORE'> = [
    'POWER_RANK'
  ]

  if (scoringData?.consistencyScore != null) {
    pointerKinds.push('CONSISTENCY_SCORE')
  }
  if (scoringData?.recencyScore != null) {
    pointerKinds.push('RECENCY_SCORE')
  }
  if (scoringData?.volatilityScore != null) {
    pointerKinds.push('VOLATILITY_SCORE')
  }

  return (
    <>
      {pointerKinds.map((pointerKind, pointerIndex) => {
        const index = startIndex + pointerIndex
        const borderBottom = pointerIndex < pointerKinds.length - 1

        if (pointerKind === 'POWER_RANK') {
          return (
            <PowerRankPointer
              key={pointerKind}
              index={index}
              borderBottom={borderBottom}
              categoryLabel={categoryLabel}
              powerRank={planDetails?.powerRank ?? null}
              powerRating={planDetails?.powerRating ?? ''}
              totalCategoryCount={planDetails?.totalCategoryCount ?? null}
            />
          )
        }

        if (pointerKind === 'CONSISTENCY_SCORE') {
          return (
            <ConsistencyScorePointer
              key={pointerKind}
              index={index}
              borderBottom={borderBottom}
              categoryLabel={categoryLabel}
              score={scoringData?.consistencyScore}
              maxScore={maxScore}
              performanceTag={scoringData?.consistencyScorePerformanceTag ?? ''}
            />
          )
        }

        if (pointerKind === 'RECENCY_SCORE') {
          return (
            <RecencyScorePointer
              key={pointerKind}
              index={index}
              borderBottom={borderBottom}
              categoryLabel={categoryLabel}
              score={scoringData?.recencyScore}
              maxScore={maxScore}
              performanceTag={scoringData?.recencyScorePerformanceTag ?? ''}
            />
          )
        }

        return (
          <VolatilityScorePointer
            key={pointerKind}
            index={index}
            borderBottom={borderBottom}
            categoryLabel={categoryLabel}
            score={scoringData?.volatilityScore}
            maxScore={maxScore}
            performanceTag={scoringData?.volatilityScorePerformanceTag ?? ''}
          />
        )
      })}
    </>
  )
}

type SipStopBetterFundPointerProps = {
  planDetails: SipPlanRecommendationDetails | null
  subtitle: string
}

export function SipStopBetterFundPointer({ planDetails, subtitle }: SipStopBetterFundPointerProps) {
  const categoryLabel = planDetails?.categoryName ?? ''

  return (
    <PlanRecommendationReasonPointer
      index={1}
      badgeLabel={resolvePlanRecommendationReasonPointerBadgeLabel({
        totalPointerCount: 1,
        showsPercentageHighlight: true
      })}
      title={(
        <>
          <span>We&apos;ve recommended a better fund</span>
          <br />
          <span className="font-[400]">{`for SIP in ${categoryLabel}`}</span>
        </>
      )}
      subtitle={subtitle}
      subtitleClassName="!text-[#6A6E6E]"
    />
  )
}
